import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  InputSelect,
} from '@features/ui';
import { FC, useCallback, useEffect } from 'react';
import {
  TTradingPlatformFormData,
  schemaTradingPlatform,
  TradingPlatForms,
} from '@src/types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTradingPlatformDetail,
  saveTradingPlatform,
} from '@src/services/tradingPlatforms.ts';
import { toast } from 'react-toastify';
import { EditorComponent } from '@src/features/admin/emails/EditorComponent';

interface TradingPlatformDetailProps {
  isEdit?: boolean;
}

const trading_platform_type_options = [
  {
    value: TradingPlatForms.CQG_DATA_FEED,
    label: 'CQG Data Feed',
  },
  {
    value: TradingPlatForms.RITHMIC_DATA_FEED,
    label: 'Rithmic Data Feed',
  },
  // {
  //   value: TradingPlatForms.DX_DATA_FEED,
  //   label: 'DX Data Feed',
  // },
];

export const TradingPlatformDetail: FC<TradingPlatformDetailProps> = ({
  isEdit,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TTradingPlatformFormData>({
    resolver: yupResolver(schemaTradingPlatform),
  });
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEdit || !params?.id) return;
    getTradingPlatformDetail(Number(params?.id)).then(({ data }) => {
      reset({
        name: data.name,
        type: data.type ?? undefined,
        content: data.content ?? '',
        is_publish: data.is_publish ?? false,
      });
    });
  }, [isEdit, params?.id]);

  const handleSubmitForm = useCallback(
    async (data: TTradingPlatformFormData) => {
      try {
        const payload = {
          id: isEdit && params.id ? Number(params.id) : undefined,
          ...data,
        };

        await saveTradingPlatform(payload);
        toast.success(
          `Trading platform ${isEdit ? 'updated' : 'created'} successfully`
        );
        if (!isEdit) {
          navigate(`/admin/trading-platforms`);
        }
      } catch {
        toast.error('Something went wrong. Please try again later.');
      }
    },
    [isEdit, navigate, params.id]
  );

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
      <div className={'flex justify-between items-center'}>
        <div className={'flex gap-x-2 text-[23px]'}>
          <p className={'text-blue'}>Trading Platform</p>
          <p>|</p>
          <p>{`${isEdit ? 'Edit' : 'Create'} Trading Platform`}</p>
        </div>
      </div>
      <Card className={'mt-4.5 mb-5 text-xs space-y-7'}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <div className={'flex flex-col gap-y-1'}>
              <Label className={'text-xs mb-1'}>Name</Label>
              <Input
                placeholder={'Enter'}
                textSize={'xs'}
                variant={'secondary'}
                {...field}
              />
              <p className={'text-red-500'}>{errors.name?.message}</p>
            </div>
          )}
        />
        <Controller
          control={control}
          name="type"
          render={({ field }) => {
            return (
              <div className={'flex flex-col gap-y-1'}>
                <Label className={'text-xs mb-1'}>Type</Label>
                <InputSelect
                  options={trading_platform_type_options}
                  placeholder={'Select...'}
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                />
              </div>
            );
          }}
        />
      </Card>
      <Card className={'flex flex-col gap-y-5.5'}>
        <p className={'text-sm'}>Content</p>
        <Controller
          control={control}
          name="content"
          render={({ field }) => (
            <div className={'flex flex-col gap-y-1'}>
              <EditorComponent {...field} />
              {errors.content?.message ? (
                <p className={'text-red-500 text-xs'}>
                  {errors.content?.message}
                </p>
              ) : null}
            </div>
          )}
        />
        <Controller
          control={control}
          name="is_publish"
          render={({ field }) => (
            <div className={'text-xs flex flex-col gap-y-2.5 font-semibold'}>
              <p>Published ?</p>
              <div className={'flex items-center gap-x-3'}>
                <Checkbox
                  checked={!!field.value}
                  className={'border-[white] border-[1px] h-5 w-5'}
                  label={'Yes'}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </div>
            </div>
          )}
        />
      </Card>
      <div className="flex items-center gap-3 pt-4">
        <Button
          loading={isSubmitting}
          size={'sm'}
          type="submit"
          variant={'lime'}
        >
          Save
        </Button>
        <Button
          size={'sm'}
          type="button"
          variant={'secondary'}
          onClick={() => navigate('/admin/trading-platforms')}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
